/**
 * Any other country: a country code, a bank identifier and an account number, without a format.
 * Normalized to upper-case letters and digits only. No IBAN.
 * @module banking/schemes/other
 */

import { BbanStructure } from 'ibankit';
import type { BankingValidationError } from '../validation';
import { BankAccountScheme, type BankAccountSchemeDefinition } from './bank-account-scheme';
import { GB_SORT_CODE_COUNTRIES } from './gb-sort-code.scheme';
import { US_ABA_COUNTRIES } from './us-aba.scheme';
import { bicErrors, normalizeBic, normalizeCountryCode, REQUIRED_ERRORS } from './scheme-helpers';

/** A country that has its own scheme: every IBAN country and the GB and US territories. */
function hasOwnScheme(countryCode: string): boolean {
  return (
    BbanStructure.forCountry(countryCode) !== null ||
    GB_SORT_CODE_COUNTRIES.includes(countryCode) ||
    US_ABA_COUNTRIES.includes(countryCode)
  );
}

function lettersAndDigits(value?: string | null): string | null {
  const clean = value
    ?.normalize('NFKC')
    .toUpperCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
  return clean ? clean : null;
}

/** Longest normalized bank identifier; the stored column is `varchar(20)`. */
export const OTHER_BANK_IDENTIFIER_MAX_LENGTH = 20;
/** Longest normalized account number; the stored column is `varchar(50)`. */
export const OTHER_ACCOUNT_NUMBER_MAX_LENGTH = 50;

export const otherScheme: BankAccountSchemeDefinition = {
  scheme: BankAccountScheme.OTHER,

  validate(account) {
    const errors: BankingValidationError[] = [];
    const countryCode = normalizeCountryCode(account.countryCode);
    if (!countryCode) {
      errors.push({ code: 'countryCodeInvalid', message: 'Country must be a two-letter code' });
    } else if (hasOwnScheme(countryCode)) {
      errors.push({
        code: 'schemeRequiredForCountry',
        message: `Use the bank account scheme of ${countryCode}`
      });
    }
    const bankIdentifier = lettersAndDigits(account.bankIdentifier);
    if (!bankIdentifier) {
      errors.push(REQUIRED_ERRORS.bankIdentifier);
    } else if (bankIdentifier.length > OTHER_BANK_IDENTIFIER_MAX_LENGTH) {
      errors.push({
        code: 'bankIdentifierInvalid',
        message: `Bank identifier must be at most ${OTHER_BANK_IDENTIFIER_MAX_LENGTH} letters and digits`
      });
    }
    const accountNumber = lettersAndDigits(account.accountNumber);
    if (!accountNumber) {
      errors.push(REQUIRED_ERRORS.accountNumber);
    } else if (accountNumber.length > OTHER_ACCOUNT_NUMBER_MAX_LENGTH) {
      errors.push({
        code: 'accountNumberInvalid',
        message: `Account number must be at most ${OTHER_ACCOUNT_NUMBER_MAX_LENGTH} letters and digits`
      });
    }
    errors.push(...bicErrors(account.bic));

    return { valid: errors.length === 0, errors };
  },

  normalize(account) {
    const countryCode = normalizeCountryCode(account.countryCode);
    const bankIdentifier = lettersAndDigits(account.bankIdentifier);
    const accountNumber = lettersAndDigits(account.accountNumber);
    if (!countryCode || !bankIdentifier || !accountNumber) return null;
    if (
      bankIdentifier.length > OTHER_BANK_IDENTIFIER_MAX_LENGTH ||
      accountNumber.length > OTHER_ACCOUNT_NUMBER_MAX_LENGTH
    ) {
      return null;
    }

    return {
      scheme: BankAccountScheme.OTHER,
      countryCode,
      iban: null,
      bankIdentifier,
      accountNumber,
      bic: normalizeBic(account.bic)
    };
  },

  toIban() {
    return null;
  }
};
